import React, { useState } from "react";
import { parseEventScore, formatEventScore } from "../utils/scoreUtils";
import { db } from "../firebase/config";
import { collection, getDocs, query, where, addDoc } from "firebase/firestore";
import Logo from "../components/assets/logo-icon.png";

const eventOptions = ["bench", "squat", "run", "swim", "shots"];

const Upload: React.FC = () => {
  const [email, setEmail] = useState("");
  const [birthday, setBirthday] = useState("");
  const [validated, setValidated] = useState(false);
  const [athlete, setAthlete] = useState<any>(null);

  const [event, setEvent] = useState("bench");
  const [date, setDate] = useState("");
  const [result, setResult] = useState("");
  const [status, setStatus] = useState("");

  const validateAthlete = async () => {
    setStatus("Checking...");
    const q = query(collection(db, "athletes"), where("email", "==", email));
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      setStatus("No athlete found with that email.");
      return;
    }

    const docData = snapshot.docs[0].data();
    const storedDate = new Date(
      docData.birthday?.seconds
        ? docData.birthday.seconds * 1000
        : docData.birthday
    );
    const inputDate = new Date(birthday);

    if (
      storedDate &&
      storedDate.getFullYear() === inputDate.getFullYear() &&
      storedDate.getMonth() === inputDate.getMonth() &&
      storedDate.getDate() === inputDate.getDate()
    ) {
      setAthlete(docData);
      setValidated(true);
      setStatus("");
    } else {
      setStatus("Birthday does not match.");
    }
  };

  const handleSignOut = () => {
    setValidated(false);
    setAthlete(null);
    setStatus("Signed out.");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!athlete) return;

    const d = new Date(date);
    const score = parseEventScore(event, result);

    const currentYear = new Date().getFullYear();
    if (d.getFullYear() !== currentYear) {
      setStatus("Only entries from the current year are accepted.");
      return;
    }

    if (
      ["shots"].includes(event) &&
      (!Number.isInteger(score) || score < 0 || score > 30)
    ) {
      setStatus("Shots must be a whole number between 0 and 30.");
      return;
    }

    if (["bench", "squat"].includes(event) && (score < 0 || score >= 1000)) {
      setStatus("Bench and squat must be less than 1000.");
      return;
    }

    if (["run", "swim"].includes(event)) {
      const validTimeFormat = /^(\d+:\d{2}(\.\d{1,2})?|\d{1,3}(\.\d{1,2})?)$/;
      if (!validTimeFormat.test(result.trim())) {
        setStatus("Run and swim must be in SS.SS or M:SS.SS format.");
        return;
      }
    }

    if (isNaN(score)) {
      setStatus("Invalid result: please follow the format instructions.");
      return;
    }

    const birthDate = new Date(athlete.birthday);
    const age = Math.floor(
      (d.getTime() - birthDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000)
    );

    const newEntry = {
      year: d.getFullYear(),
      month: d.getMonth() + 1,
      day: d.getUTCDate(),
      age,
      event,
      firstName: athlete.firstName,
      lastName: athlete.lastName,
      gender: athlete.gender,
      email: athlete.email,
      score,
      result,
      uploadStamp: new Date(),
    };

    try {
      await addDoc(collection(db, "entries"), newEntry);
      setStatus("✅ Entry uploaded!");
      setEvent("bench");
      setDate("");
      setResult("");
    } catch (err) {
      console.error(err);
      setStatus("❌ Upload failed. Try again.");
    }
  };

  return (
    <div className="home-page-container">
      <nav className="navbar">
        <div className="navbar-content">
          <img className="site-logo" src={Logo} alt="Cutter Games Logo" />
          <div className="site-header">
            <h1 className="site-title">Cutter Games</h1>
          </div>
          <ul className="nav-links">
            <li>
              <a href="/" className="nav-link">
                Home
              </a>
            </li>
            <li>
              <a href="/about" className="nav-link">
                About
              </a>
            </li>
            <li>
              <a href="/upload" className="nav-link">
                Upload
              </a>
            </li>
          </ul>
        </div>
      </nav>
      <section className="results-list-section">
        {!validated ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              validateAthlete();
            }}
          >
            <div className="row">
              <div className="filter-label">Email</div>
              <div className="filter-label">Birthday</div>
            </div>
            <div className="row gap">
              <div className="filter-group">
                <input
                  className="filter-button"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="filter-group">
                <input
                  className="filter-button"
                  type="date"
                  value={birthday}
                  onChange={(e) => setBirthday(e.target.value)}
                  required
                />
              </div>
            </div>
            <button className="filter-button full" type="submit">
              Validate Identity
            </button>
            <p className="status-text">{status}</p>
          </form>
        ) : (
          <form onSubmit={handleSubmit}>
            <h2 className="section-title full">
              {athlete.firstName} {athlete.lastName} Entry Upload
            </h2>
            <div className="filter-label">Event</div>
            <div className="row gap">
              <div className="filter-group">
                <select
                  className="filter-button"
                  value={event}
                  onChange={(e) => setEvent(e.target.value)}
                >
                  {eventOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt.charAt(0).toUpperCase() + opt.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="row">
              <div className="filter-label">Date of Performance</div>
              <div className="filter-label">Result</div>
            </div>
            <div className="row gap">
              <div className="filter-group">
                <input
                  className="filter-button"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  required
                />
              </div>
              <div className="filter-group">
                <input
                  className="filter-button"
                  type="text"
                  value={result}
                  onChange={(e) => setResult(e.target.value)}
                  placeholder="Result Input"
                  required
                />
              </div>
            </div>
            <small>
              <p>
                <strong>Bench / Squat:</strong> Enter weight in pounds no units
                (e.g. 185.5).
              </p>
              <p>
                <strong>Run / Swim:</strong> Use <code>M:SS.SS</code> (e.g.
                1:23.45) or <code>M:SS</code> (e.g. 2:34).
              </p>
              <p>
                <strong>Shots:</strong> Enter a whole number from 0 to 30.
              </p>
            </small>
            <div className="row top-gap">
              <button className="filter-button" type="submit">
                Submit Entry
              </button>
              <button
                className="filter-button"
                type="button"
                onClick={handleSignOut}
              >
                Sign Out
              </button>
            </div>
            <p className="status-text">{status}</p>
          </form>
        )}
      </section>
      <p className="honor-system-warning">
        Cutter Games relies on the honor system to keep our leaderboards
        meaningful and fair. Submitting dishonest results undermines the spirit
        of competition and the trust of your peers. All entries are reviewed
        regularly, and false reporting will result in removal from present and
        future participation. If you notice an error or need to report a
        suspicious result, please contact Christian Cutter at{" "}
        christianbcutter@yahoo.com.
      </p>
    </div>
  );
};

export default Upload;
